import { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Snackbar,
  Stack,
} from "@mui/material";
import EventBusyIcon from "@mui/icons-material/EventBusy";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import useHome from "./hooks/useHome";
import Ranking from "./components/Ranking";
import CurrentMatch from "./components/CurrentMatch";

const Home = () => {
  const {
    initialize,
    error,
    loading,
    rankings,
    selectedRankingId,
    setSelectedRankingId,
    topThreeData,
    matches,
    isWithoutSeason,
    emptySeasonMessage,
    resultDraft,
    isEditingResults,
    hasSubmittedResults,
    canSubmitAllResults,
    hasEditableMatches,
    handleEditResults,
    handleDraftChange,
    handleSaveResults,
    rankingId,
    roundEndsAt,
    isAvailable,
    setAvailability,
  } = useHome();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [availabilityLoading, setAvailabilityLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  useEffect(() => {
    initialize();
  }, [initialize]);

  const handleConfirmAvailability = useCallback(async () => {
    setAvailabilityLoading(true);
    const success = await setAvailability(!isAvailable);
    setAvailabilityLoading(false);
    setDialogOpen(false);
    setSnackbar({
      open: true,
      message: success
        ? "Disponibilidade atualizada com sucesso."
        : "Erro ao atualizar disponibilidade.",
      severity: success ? "success" : "error",
    });
  }, [isAvailable, setAvailability]);

  return (
    <Stack spacing={2}>
      {rankings.length > 1 ? (
        <FormControl fullWidth size="small">
          <InputLabel id="home-ranking-select-label">Ranking</InputLabel>
          <Select
            labelId="home-ranking-select-label"
            label="Ranking"
            value={selectedRankingId || ""}
            onChange={(event) => setSelectedRankingId(event.target.value)}
          >
            {rankings.map((r) => (
              <MenuItem key={r.id} value={r.id}>
                {r.title}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      ) : null}

      {rankingId && isAvailable !== null && (
        <Button
          variant="outlined"
          color={isAvailable ? "warning" : "success"}
          startIcon={isAvailable ? <EventBusyIcon /> : <EventAvailableIcon />}
          onClick={() => setDialogOpen(true)}
          sx={{ alignSelf: "center" }}
        >
          {isAvailable ? "Não jogarei a próxima rodada" : "Jogarei a próxima rodada"}
        </Button>
      )}

      <CurrentMatch
        canSubmitAllResults={canSubmitAllResults}
        hasEditableMatches={hasEditableMatches}
        handleDraftChange={handleDraftChange}
        handleEditResults={handleEditResults}
        handleSaveResults={handleSaveResults}
        hasSubmittedResults={hasSubmittedResults}
        isEditingResults={isEditingResults}
        isWithoutSeason={isWithoutSeason}
        loading={loading}
        matches={matches}
        emptySeasonMessage={emptySeasonMessage}
        resultDraft={resultDraft}
        error={error}
        roundEndsAt={roundEndsAt}
      />
      <Ranking
        topThreeData={topThreeData}
        loading={loading}
        error={error}
        isWithoutSeason={isWithoutSeason}
        emptySeasonMessage={emptySeasonMessage}
      />

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)}>
        <DialogTitle sx={{ fontWeight: 700 }}>
          {isAvailable ? "Confirmar ausência" : "Confirmar presença"}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {isAvailable
              ? "Tem certeza que não jogará a próxima rodada? Sua disponibilidade será marcada como indisponível."
              : "Tem certeza que jogará a próxima rodada? Sua disponibilidade será marcada como disponível."}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} disabled={availabilityLoading}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            color={isAvailable ? "warning" : "success"}
            onClick={handleConfirmAvailability}
            disabled={availabilityLoading}
            startIcon={availabilityLoading ? <CircularProgress size={16} color="inherit" /> : null}
          >
            Confirmar
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Stack>
  );
};

export default Home;
